define(
    "diy/undo-redo/SessionManager",
    [
        'jquery',
        'diy/undo-redo/constants',
        'diy/util/UUID'
    ],
    /**
     *
     * @param {jquery} $
     * @param {module.undoRedo.constants} constants
     * @param {diy.util.UUID} UUID
     * @returns {*}
     */
    function ($, constants, UUID) {
        'use strict';
        var SessionManager;

        /**
         * @class module.undoRedo.SessionManager
         * @template module.undoRedo.SessionManager
         * @lends {module.undoRedo.SessionManager.prototype}
         * @constructor
         */
        SessionManager = Klazz.extend({

            /**
             * @type {module.undoRedo.App}
             */
            app: null,

            /**
             * @type {module.undoRedo.SessionFactory}
             */
            factory: null,
            /**
             * @type {module.undoRedo.Storage}
             */
            storage: null,
            /**
             * @type {*<module.undoRedo.Session>}
             */
            sessions: {},
            /**
             * @type {module.undoRedo.Session}
             */
            activeSession: null,
            /**
             *
             * @param {module.undoRedo.App} app
             * @param {module.undoRedo.SessionFactory} sessionFactory
             */
            initialize: function (app, sessionFactory) {
                this.app = app;
                this.factory = sessionFactory;
                this.sessions = {};
            },
            /**
             *
             * @param {module.undoRedo.Storage} storage
             * @returns {module.undoRedo.SessionManager}
             */
            setStorage: function setStorage(storage) {
                this.storage = storage;
                return this;
            },
            /**
             *
             * @returns {module.undoRedo.Session}
             */
            openSession: function openSession() {
                var session = this.factory.build(this);
                this.sessions[session.getKey()] = session;
                return session;
            },
            /**
             *
             * @param {String} sessionKey
             */
            getSessionIdForKey: function getSessionIdForKey(sessionKey) {
                if (typeof this.sessions[sessionKey] === 'undefined') {
                    throw new Error('Session ' + sessionKey + ' does not exist');
                }

                if (this.activeSession !== null && this.activeSession.getKey() !== sessionKey) {
                    var oldSession = $.extend(true, {}, this.activeSession);
                    this.sessions[sessionKey].setSessionId(UUID.generate());
                    this.activeSession = this.sessions[sessionKey];

                    this.sessionClose(oldSession)
                        .then(this.setActiveSession.bind(this, this.activeSession));
                }
                if (this.activeSession === null) {
                    this.sessions[sessionKey].setSessionId(UUID.generate());
                    this.activeSession = this.sessions[sessionKey];
                    this.setActiveSession(this.activeSession);
                }


                return this.activeSession.getSessionId();
            },

            getSessionForSessionId: function getSessionForSessionId (sessionId) {
                var session = null, sessionKey;

                for (sessionKey in this.sessions) {
                    if (this.sessions.hasOwnProperty(sessionKey)) {
                        if (this.sessions[sessionKey].getSessionId() === sessionId) {
                            session = this.sessions[sessionKey];
                        }
                    }
                }
                return session;
            },
            /**
             *
             * @param sessionId
             * @returns {module.undoRedo.Session}
             */
            getOrCreateSession: function getSession(sessionId) {
                var session = this.getSessionForSessionId(sessionId);

                if(session === null) {
                    session = this.factory.build(this);
                    session.setSessionId(sessionId);
                    this.sessions[sessionId] = session;
                }

                return session;
            },

            /**
             *
             * @param {module.undoRedo.Session} session
             * @returns {Q.Promise<T>}
             */
            setActiveSession: function setActiveSession(session) {
                var deferred = Q.defer();
                this.storage.setActiveUndoSession(session.sleep())
                    .then(function () {
                        this.activeSession = session;
                        deferred.resolve(session);
                    }.bind(this))
                    .catch(function (error) {
                        deferred.reject(error);
                    });

                return deferred.promise;
            },
            /**
             *
             * @returns {Q.Promise<T>}
             */
            recoverActiveSession: function recoverActiveSession() {
                var deferred = Q.defer();

                this.getLastActiveSession()
                    .then(function (sessionData) {
                        var session;
                        if (sessionData !== null) {
                            session = this.factory.load(this, sessionData);
                            if (session.isVolatile()) {
                                session.close()
                                    .then(function () {
                                        deferred.resolve(true);
                                    })
                                    .catch(function (error) {
                                        deferred.reject(error);
                                    });
                            } else {
                                deferred.resolve(true);
                            }
                        } else {
                            deferred.resolve(true);
                        }
                    }.bind(this))
                    .catch(function (error) {
                        deferred.reject(error);
                    });
                return deferred.promise;
            },
            /**
             *
             * @param {module.undoRedo.Session} session
             * @param {boolean} noWait
             */
            closeSession: function closeSession(session, noWait) {
                var deferred = Q.defer();

                if (session === null) {
                    deferred.resolve(true);
                    return deferred.promise;
                }

                if (session.isVolatile()) {
                    this.app.deleteEntriesWithUndoSession(session.getSessionId(), noWait)
                        .then(function(){
                            this.sessionClose(session)
                                .then(function (res){
                                    deferred.resolve(res);
                                })
                                .catch(function(err){
                                    deferred.reject(err);
                                });
                        }.bind(this))
                        .catch(function(err){
                            deferred.reject(err);
                        });
                } else {
                    this.sessionClose(session)
                        .then(function(res){
                            deferred.resolve(res);
                        })
                        .catch(function(err){
                            deferred.reject(err);
                        });
                }
                return deferred.promise;
            },
            /**
             *
             * @param {module.undoRedo.Session} session
             * @private
             * @returns {Q.Promise<T>}
             */
            sessionClose: function sessionClose(session) {
                var deferred = Q.defer(),
                    doCleanup = function cleanup() {
                        delete this.sessions[session.getKey()];
                        deferred.resolve(true);
                    }.bind(this);

                if (this.activeSession === session) {
                    this.storage.setActiveUndoSession(null)
                        .then(function () {
                            this.activeSession = null;
                            doCleanup();
                        }.bind(this));
                } else {
                    doCleanup();
                }

                return deferred.promise;
            },

            /**
             *
             * @private
             * @returns {Q.Promise<T>}
             */
            getLastActiveSession: function getLastActiveSession() {
                var deferred = Q.defer();

                this.storage.getActiveUndoSession()
                    .then(function (sessionData) {
                        deferred.resolve(sessionData);
                    })
                    .catch(function (error) {
                        deferred.reject(error);
                    });

                return deferred.promise;
            },
            /**
             *
             * @returns {module.undoRedo.Session}
             */
            getActiveSession: function getActiveSession(){
                return this.activeSession;
            },

            /**
             *
             * @returns {String|null}
             */
            getActiveSessionId: function getActiveSessionId() {
                if (this.activeSession !== null) {
                    return this.getActiveSession().getSessionId();
                } else {
                    return null;
                }
            },
            /**
             *
             * @param {{undo:[], redo:[], last:{}}} incoming
             * @deprecated
             */
            syncCurrentSession: function syncCurrentSession(incoming) {
                var activeSession = this.getActiveSession(), undo = [], redo = [];

                if (activeSession){
                    this.filterActiveSession(incoming.undo, activeSession.getSessionId()).forEach(
                        function (entry) {
                            if (entry.getExecutionType() === constants.executionType.CLIENT) {
                                undo.push(entry.getKey());
                            }
                        }
                    );
                    this.filterActiveSession(incoming.redo, activeSession.getSessionId()).forEach(
                        function (entry) {
                            if (entry.getExecutionType() === constants.executionType.CLIENT) {
                                redo.push(entry.getKey());
                            }
                        }
                    );
                    activeSession
                        .setUndo(undo)
                        .setRedo(redo)
                    ;
                }
            },
            /**
             *
             * @param {module.undoRedo.Entry[]} chain
             * @param {String} activeSessionId
             * @returns {module.undoRedo.Entry[]}
             */
            filterActiveSession: function filter(chain, activeSessionId) {
                return chain.filter(function (entry /*, index, array */) {
                    return (entry.getUndoSessionId() === activeSessionId);
                });
            },

            /**
             *
             * @param {module.undoRedo.Entry} entry
             * @param {module.undoRedo.constants.types.UNDO|module.undoRedo.constants.types.REDO=} type
             */
            markInSession: function markInSession (entry, type) {
                var session, entryKey;

                if (entry.getExecutionType() === constants.executionType.CLIENT) {
                    session = this.getOrCreateSession(entry.getUndoSessionId());
                    entryKey = entry.getKey();

                    session.undo = session.undo.filter(function(key) { return (key !== entryKey); });
                    session.redo = session.redo.filter(function(key) { return (key !== entryKey); });

                    type = (typeof type !== 'undefined')? type : entry.getType();

                    switch (type) {
                        case constants.types.UNDO:
                            session.undoAdd(entryKey);
                        break;
                        case constants.types.REDO:
                            session.redoAdd(entryKey);
                        break;
                        case null:
                            // nothing will happen, reference will be deleted
                        break;
                    }
                }
            }

        });

        return SessionManager;
    }
);



/*****************
 ** WEBPACK FOOTER
 ** ./source/cdn/document_root/s/diy/undo-redo/SessionManager.js
 ** module id = 895
 ** module chunks = 0
 **/